// src/contexts/AuthContext.js - Handle password change requirement
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signIn, signOut, confirmSignIn } from 'aws-amplify/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const user = await getCurrentUser();
      console.log('User found in checkUser:', user);
      setUser(user);
    } catch (error) {
      console.log('No user found in checkUser');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const signInResult = await signIn({ username: email, password });
      console.log('Sign in result:', signInResult);
      
      if (signInResult.isSignedIn) {
        const user = await getCurrentUser();
        setUser(user);
        return { success: true };
      } else if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        console.log('Password change required');
        
        // For demo purposes, let's use the same password as the new password
        // In production, you'd want a proper password change form
        try {
          const confirmResult = await confirmSignIn({ challengeResponse: password });
          if (confirmResult.isSignedIn) {
            const user = await getCurrentUser();
            setUser(user);
            return { success: true };
          }
        } catch (confirmError) {
          console.error('Password confirmation failed:', confirmError);
          return { success: false, error: 'Password change required. Please contact support.' };
        }
      }
      
      return { success: false, error: 'Sign-in incomplete' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}